import { getTickets as getTicketsApi } from "@/api/ticket";
import MessageInputBox from "@/components/Ticket/MessageInputBox";
import MessageList from "@/components/Ticket/MessageList";
import { TicketInput } from "@/components/Ticket/TicketInput";
import { TicketList } from "@/components/Ticket/TicketList";
import { TicketSearch } from "@/components/Ticket/TicketSearch";
import { TabStatus, Ticket } from "@/components/Ticket/types";
import { useLoadingStore } from "@/stores/loading";
import { useMessageModalStore } from "@/stores/message";
import { useUserStore } from "@/stores/user";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";

type Props = {
  isType: TicketPageType;
};

export enum TicketPageType {
  List = "list",
  New = "new",
  Detail = "detail",
  Search = "search",
}

export default function MypageTicket({ isType }: Props) {
  const [user, fetchUser] = useUserStore((state) => [
    state.user,
    state.fetchUser,
  ]);
  const setLoading = useLoadingStore((state) => state.setLoading);
  const setMessage = useMessageModalStore((state) => state.setMessage);
  const { ticketId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentStatus = searchParams.get("status");

  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [tabStatuses, setTabStatuses] = useState<TabStatus[] | null>(null);

  const isDetail = isType === TicketPageType.Detail;
  const isNew = isType === TicketPageType.New;
  const isSearch = isType === TicketPageType.Search;

  const getTicket = useCallback(() => {
    setLoading(true);
    getTickets();
  }, [user]);

  const currentTicket = useMemo(() => {
    if (!isDetail) return;
    return tickets.find((t) => String(t.data_ticket_id) === ticketId);
  }, [ticketId, tickets]);

  const getTickets = useCallback(() => {
    if (!user?.id) return;

    getTicketsApi({ customer_id: user.id, tab_status: currentStatus })
      .then((result) => {
        setTickets(result.data.data);
        setTabStatuses(result.data.tab_statuses);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [user?.id, searchParams]);

  useEffect(() => {
    if (user?.id) return;

    fetchUser(setLoading, () => {});
  }, []);

  useEffect(() => {
    if (user?.id) {
      setLoading(true);
      getTickets();
    }
  }, [user?.id, searchParams, ticketId, isType]);

  if (!user?.id) {
    return null; 
  }

  return (
    <div>
      <div className="sub-header">
        <ul className="uk-tab uk-margin">
          <li>
            <Link to="/drm_pf/api">利用料</Link>
          </li>
          <li>
            <Link to="/basic_info">基本情報</Link>
          </li>
          <li>
            <Link to="/drm_pf/info">DRM-PF</Link>
          </li>
          <li className="uk-active">
            <Link to="/ticket">DRM-Chat</Link>
          </li>
        </ul>
      </div>
      <div className="container ticket-container">
        {isNew ? (
          <TicketInput />
        ) : isSearch ? (
          <TicketSearch />
        ) : (
          <div className="ticket-detail-container">
            {isDetail ? (
              <MessageList ticket={currentTicket} onCommentUpdated={getTicket} />
            ) : (
              tickets?.map((t) => (
                <MessageList
                  ticket={t}
                  key={`ticket-message-list-${t.data_ticket_id}`}
                  onCommentUpdated={getTicket}
                />
              ))
            )}
            {isDetail && tabStatuses && (
              <MessageInputBox
                ticket={currentTicket}
                getTicket={getTicket}
                customer={user}
                tabStatuses={tabStatuses}
              />
            )}
          </div>
        )}
        <div className="ticket-list-container">
          {tabStatuses && (
            <TicketList tickets={tickets} tabStatuses={tabStatuses} />
          )}
        </div>
      </div>
    </div>
  );
}
